import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import type { BriefPriorityIncident } from '../../data/types';
import { AgentBadge } from './AgentBadge';
import { cn } from '../../lib/utils';

interface PriorityQueueProps {
  incidents: BriefPriorityIncident[];
  currentUserName: string;
}

type Tab = 'priority' | 'yours' | 'unassigned';

export function PriorityQueue({ incidents, currentUserName }: PriorityQueueProps) {
  const [tab, setTab] = useState<Tab>('priority');

  const filtered = useMemo(() => {
    if (tab === 'yours') return incidents.filter(i => i.assignee === 'you');
    if (tab === 'unassigned') return incidents.filter(i => i.assignee === '—');
    return incidents;
  }, [incidents, tab]);

  return (
    <section className="lg:col-span-8 bg-bg-secondary border border-border-default rounded-xl overflow-hidden flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-border-light gap-2.5">
        <h3 className="font-sans font-medium text-[12.5px] tracking-[-0.005em] flex items-center gap-2 text-text-primary m-0">
          Priority queue
          <span className="font-mono text-[10.5px] text-text-secondary px-1.5 py-px bg-bg-tertiary rounded border border-border-default font-normal">
            {incidents.length} flagged
          </span>
        </h3>
        <div className="flex gap-0.5">
          {(['priority', 'yours', 'unassigned'] as Tab[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'px-2.5 py-1.5 text-[11.5px] font-sans font-medium rounded border-0 cursor-pointer transition-colors',
                tab === t
                  ? 'text-text-primary bg-bg-tertiary'
                  : 'text-text-secondary bg-transparent hover:bg-bg-tertiary'
              )}
            >
              {t === 'priority' ? 'Priority' : t === 'yours' ? 'Yours' : 'Unassigned'}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1">
        {filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-text-secondary text-sm">
            Nothing in this view.
          </div>
        ) : (
          filtered.map((inc, i) => (
            <IncidentRow
              key={`${inc.tenantId}-${inc.incidentId}`}
              inc={inc}
              isLast={i === filtered.length - 1}
              currentUserName={currentUserName}
            />
          ))
        )}
      </div>

      <footer className="px-4 py-2 border-t border-border-light text-[11px] text-text-secondary font-sans flex items-center gap-2 bg-bg-tertiary/40">
        <AgentBadge name="Triage" /> ranked by severity, SLA proximity, and incident age.
      </footer>
    </section>
  );
}

const SEV_BG: Record<BriefPriorityIncident['severity'], string> = {
  critical: 'bg-brand-navy',
  high: 'bg-navy-semi-light',
  medium: 'bg-brand-blue',
  low: 'bg-neutral-semi-light',
  informational: 'bg-neutral-semi-light',
};

function IncidentRow({
  inc,
  isLast,
  currentUserName: _currentUserName,
}: {
  inc: BriefPriorityIncident;
  isLast: boolean;
  currentUserName: string;
}) {
  const slaLabel = formatSla(inc);

  return (
    <Link
      to={`/incidents/${inc.tenantId}/${inc.incidentId}`}
      className={cn(
        'block px-4 py-3.5 hover:bg-bg-tertiary/50 transition-colors',
        !isLast && 'border-b border-border-light'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'w-[22px] h-[22px] rounded grid place-items-center text-white font-sans text-[11px] font-semibold flex-shrink-0 mt-0.5',
            SEV_BG[inc.severity]
          )}
        >
          {inc.severity[0].toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-sans font-medium text-[13.5px] tracking-[-0.005em] text-text-primary flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] text-text-secondary font-normal">{inc.incidentId}</span>
            <span className="font-mono text-[10px] bg-brand-navy text-brand-lime px-1.5 py-px rounded font-medium">
              {inc.tenantTag}
            </span>
            <span className="line-clamp-1">{inc.title}</span>
          </div>
          {inc.summary && (
            <div className="text-[12px] text-text-secondary mt-1 leading-[1.5] max-w-[70ch] line-clamp-2">
              {inc.summary}
            </div>
          )}
          <div className="flex gap-1 mt-2 flex-wrap">
            {inc.iocs.map(i => (
              <span
                key={i}
                className="font-mono text-[10.5px] bg-bg-tertiary border border-border-default px-1.5 py-0.5 rounded text-text-secondary"
              >
                {i}
              </span>
            ))}
            {inc.cluster && (
              <span className="inline-flex items-center gap-1 font-mono text-[10.5px] bg-blue-ultra-light border border-blue-light px-1.5 py-0.5 rounded text-brand-blue font-medium">
                <ArrowUpRight className="w-3 h-3" />
                {inc.cluster}
              </span>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div
            className={cn(
              'inline-block text-[10.5px] px-2 py-0.5 rounded-full font-mono',
              inc.slaBreached
                ? 'text-accent-red bg-accent-red/10'
                : 'text-text-secondary bg-bg-tertiary'
            )}
          >
            {inc.slaBreached ? `⚠ ${slaLabel}` : `SLA ${slaLabel}`}
          </div>
          <div className="text-[11px] text-text-secondary mt-1.5 font-sans">
            {inc.assignee === 'you' ? '→ you' : inc.assignee === '—' ? 'unassigned' : inc.assignee}
          </div>
          <div className="text-[10.5px] text-text-muted mt-0.5 font-mono">{formatAge(inc.ageMinutes)}</div>
        </div>
      </div>
    </Link>
  );
}

function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (hours < 24) return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  const days = Math.floor(hours / 24);
  const remH = hours % 24;
  return remH === 0 ? `${days}d` : `${days}d ${remH}h`;
}

function formatSla(inc: BriefPriorityIncident): string {
  if (inc.slaMinutesRemaining === null) return '—';
  const m = inc.slaMinutesRemaining;
  if (m < 0) return `breach ${formatAge(-m)} ago`;
  return formatAge(m);
}
